import { Insight } from '@fullstory/proto/fs/api/proactiveinsights';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import {
  Dashboard,
  FunnelDetail,
  GetAllSpacesResponse,
  GetDefaultSpaceResponse,
  GetLibraryViewResponse,
  GetSpaceCardViewResponse,
  MetricDetail,
  ObjectType,
  RetentionMetricDetail,
  SegmentDetail,
  Space,
  SpaceObject,
  SpaceSection,
} from '@fullstory/proto/fs/services/dx-objects';
import { ResourceAccessAction } from '@fullstory/proto/fs/services/grant';
import { PageInfoResponse } from '@fullstory/proto/fs/shared/pagination';
import { Timestamp, int64 } from '@fullstory/proto/google/protobuf';
import { mockDashboard, mockDashboards } from '../dashboards';
import { mockGetAllRetentionMetricsResponse, mockRetentionDetail } from '../retention';
import { mockFunnelDetail, mockGetAllFunnelsResponse } from '../savedFunnels';
import { mockGetAllMetricsResponse, mockSingleNumberMetric } from '../savedMetrics';
import { mockGetAllSegmentsResponse, mockSegmentDetail } from '../segmentService';
import { PRIVATE_AUDIT_METADATA } from '../shared';

export const DEFAULT_SPACE: Space = {
  orgId: 'test-org',
  id: 'testSpace',
  name: 'My Space',
  description: 'throwback space',
  isDefault: true,
  color: '#111111',
  recentViewers: [],
  lastUpdated: null,
};

export const SECOND_SPACE: Space = {
  orgId: 'test-org',
  id: 'testSpace2',
  name: 'Another Space',
  description: 'one more space',
  isDefault: false,
  color: '#111111',
  recentViewers: [],
  lastUpdated: null,
};

export const DEFAULT_SPACE_SECTION: SpaceSection = {
  orgId: 'test-org',
  id: 'testSection',
  name: 'First Section',
  spaceId: DEFAULT_SPACE.id,
  index: 0,
  cards: [],
};

export const SECOND_SPACE_SECTION: SpaceSection = {
  orgId: 'test-org',
  id: 'testSection2',
  name: 'Second Section',
  spaceId: DEFAULT_SPACE.id,
  index: 0,
  cards: [],
};

export const GET_ALL_SPACES_RESPONSE: GetAllSpacesResponse = {
  spaces: [DEFAULT_SPACE],
  accessInfo: {
    [DEFAULT_SPACE.id]: {
      allAccess: ResourceAccessAction.ACTION_WRITE,
      accesses: [],
    },
  },
};
export const getMultipleSpacesResponse: GetAllSpacesResponse = {
  spaces: [DEFAULT_SPACE, SECOND_SPACE],
  accessInfo: {
    [DEFAULT_SPACE.id]: {
      allAccess: ResourceAccessAction.ACTION_WRITE,
      accesses: [],
    },
    [SECOND_SPACE.id]: {
      allAccess: ResourceAccessAction.ACTION_WRITE,
      accesses: [],
    },
  },
};
export const GET_DEFAULT_SPACE_RESPONSE: GetDefaultSpaceResponse = {
  defaultSpace: DEFAULT_SPACE,
};
export const GET_LIBRARY_VIEW_RESPONSE: GetLibraryViewResponse = { objects: [], pageInfo: null };

export const metricToSpaceObject = (
  metric: MetricDetail | undefined,
  insight?: Insight,
  owningSpace: Space | null = null,
): SpaceObject => ({
  orgId: 'orgId',
  dxObjectId: metric?.metric?.metric?.id ?? '',
  dxObjectType: ObjectType.METRIC,
  metric,
  owningSpace,
  insights: insight ? [insight] : [],
  featuredIn: [],
  referencedIn: [],
  canOwn: null,
});

interface MockGetLibraryViewParams {
  orgId?: string;
  includedTypes?: ObjectType[];
  search?: string;
  pageInfo?: PageInfoResponse | null;
  owningSpace?: Space | null;
  includePrivate?: boolean;
}

/**
 * A helper for mocking out an array of library objects that can be used to test
 * library views / tables. Some good future improvements here would be extending this
 * to allow for specifying the number of each object type and more specific parameters
 * for each object.
 *
 * @param orgId The orgId for the objects. Defaults to "testOrg".
 * @param includedTypes An array of object types to include in the response. If undefined, all types will be included.
 * @returns A GetLibraryViewResponse that includes 10 objects of each type.
 */
export const mockGetLibraryViewResponse = ({
  orgId = 'testOrg',
  includedTypes,
  search,
  pageInfo = null,
  owningSpace = null,
  includePrivate = false,
}: MockGetLibraryViewParams): GetLibraryViewResponse => {
  const objects: SpaceObject[] = [];

  // Add metrics to object array
  const dashboards: SpaceObject[] = [];
  const mockedDashboards: Dashboard[] = mockDashboards();
  if (includePrivate) {
    mockedDashboards.push(
      mockDashboard({
        name: 'Private Dashboard',
        id: 'dashboardPrivate' as int64,
        visibility: Visibility.PRIVATE,
      }),
    );
  }
  for (const dashboard of mockedDashboards) {
    dashboards.push({
      orgId,
      dxObjectId: dashboard.id.toString(),
      dxObjectType: ObjectType.DASHBOARD,
      dashboard,
      owningSpace,
      insights: [],
      featuredIn: [],
      referencedIn: [],
      canOwn: null,
    });
  }
  if (includedTypes?.includes(ObjectType.DASHBOARD) || includedTypes === undefined) {
    objects.push(...dashboards);
  }

  // Add funnels to object array
  const funnels: SpaceObject[] = [];
  const mockedFunnels: FunnelDetail[] = mockGetAllFunnelsResponse({}).funnels;
  if (includePrivate) {
    mockedFunnels.push(
      mockFunnelDetail({
        id: 'funnelPrivate',
        name: 'Private Funnel',
        metadata: PRIVATE_AUDIT_METADATA,
      }),
    );
  }
  for (const funnel of mockedFunnels) {
    funnels.push({
      orgId,
      dxObjectId: funnel.funnel?.funnel?.id ?? '',
      dxObjectType: ObjectType.FUNNEL,
      funnel,
      owningSpace,
      insights: [],
      featuredIn: [],
      referencedIn: [],
      canOwn: null,
    });
  }
  if (includedTypes?.includes(ObjectType.FUNNEL) || includedTypes === undefined) {
    objects.push(...funnels);
  }

  // Add metrics to object array
  const metrics: SpaceObject[] = [];
  const mockedMetrics: MetricDetail[] = mockGetAllMetricsResponse({}).metrics;
  if (includePrivate) {
    mockedMetrics.push(
      mockSingleNumberMetric({
        metricId: 'metricPrivate',
        name: 'Private Metric',
        lastViewed: Timestamp.now(),
        metadata: PRIVATE_AUDIT_METADATA,
        starred: false,
      }),
    );
  }
  for (const metric of mockedMetrics) {
    metrics.push(metricToSpaceObject(metric, undefined, owningSpace));
  }
  if (includedTypes?.includes(ObjectType.METRIC) || includedTypes === undefined) {
    objects.push(...metrics);
  }

  // Add retention metrics to object array
  const retentionMetrics: SpaceObject[] = [];
  const mockedRetentionMetrics: RetentionMetricDetail[] = mockGetAllRetentionMetricsResponse(
    {},
  ).retentionMetrics;
  if (includePrivate) {
    mockedRetentionMetrics.push(
      mockRetentionDetail({
        id: 'retentionPrivate',
        name: 'Private Retention',
        metadata: PRIVATE_AUDIT_METADATA,
      }),
    );
  }
  for (const retentionMetric of mockedRetentionMetrics) {
    retentionMetrics.push({
      orgId,
      dxObjectId: retentionMetric.retentionMetric?.retentionMetric?.id ?? '',
      dxObjectType: ObjectType.RETENTION,
      retentionMetric,
      owningSpace,
      insights: [],
      featuredIn: [],
      referencedIn: [],
      canOwn: null,
    });
  }
  if (includedTypes?.includes(ObjectType.RETENTION) || includedTypes === undefined) {
    objects.push(...retentionMetrics);
  }

  // Add segments to object array
  const segments: SpaceObject[] = [];
  const mockedSegments: SegmentDetail[] = mockGetAllSegmentsResponse({}).segments;
  if (includePrivate) {
    mockedSegments.push(
      mockSegmentDetail({
        id: 'segmentPrivate',
        name: 'Private Segment',
        metadata: PRIVATE_AUDIT_METADATA,
      }),
    );
  }
  for (const segment of mockedSegments) {
    segments.push({
      orgId,
      dxObjectId: segment.segment?.segment?.id ?? '',
      dxObjectType: ObjectType.SEGMENT,
      segment,
      owningSpace,
      insights: [],
      featuredIn: [],
      referencedIn: [],
      canOwn: null,
    });
  }
  if (includedTypes?.includes(ObjectType.SEGMENT) || includedTypes === undefined) {
    objects.push(...segments);
  }

  // TODO Add heatmaps to object array
  // We need some helpers for mocking heatmaps
  // const heatmaps: SpaceObject[] = [];

  // TODO Add journeys to object array
  // We need some helpers for mocking journeys
  // const journeys: SpaceObject[] = [];

  if (search) {
    return {
      objects: objects.filter(
        object =>
          object.dashboard?.name?.includes(search) ||
          object.funnel?.funnel?.funnel?.name?.includes(search) ||
          object.metric?.metric?.metric?.name?.includes(search) ||
          object.retentionMetric?.retentionMetric?.retentionMetric?.name?.includes(search) ||
          object.segment?.segment?.segment?.name?.includes(search),
      ),
      pageInfo,
    };
  }

  return { objects, pageInfo };
};

export const mockGetSpaceCardViewResponse = (): GetSpaceCardViewResponse => ({
  sections: [],
  cardLimit: int64.fromNumber(45),
});
